import { useMemo } from 'react';
import type { FormSubmissionInput } from '@qdf/shared';

type PrefillData = Partial<FormSubmissionInput>;

/**
 * Reads query-string parameters and maps them to form field defaults.
 * Example: ?fullName=Jane+Doe&entityName=Doe+Properties+LLC&email=jane@example.com
 */
export function usePartnerPrefill(): PrefillData {
  return useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const prefill: PrefillData = {};

    // String field mappings (param name → form field)
    const stringMap: Record<string, keyof PrefillData> = {
      partnerSubmissionId: 'partnerSubmissionId',
      fullName: 'fullName',
      entityName: 'entityName',
      dba: 'dba',
      email: 'email',
      phone: 'phone',
      mailingAddress1: 'mailingAddress1',
      mailingAddress2: 'mailingAddress2',
      mailingCity: 'mailingCity',
      mailingState: 'mailingState',
      mailingZip: 'mailingZip',
      marketsOperating: 'marketsOperating',
      preferredTitleCompany: 'preferredTitleCompany',
    };

    for (const [param, field] of Object.entries(stringMap)) {
      const value = params.get(param);
      if (value) {
        (prefill as Record<string, string>)[field] = value;
      }
    }

    // Number field mappings
    const numberMap: Record<string, keyof PrefillData> = {
      locAmountRequested: 'locAmountRequested',
      avgAnnualTransactions: 'avgAnnualTransactions',
      avgPurchasePrice: 'avgPurchasePrice',
    };

    for (const [param, field] of Object.entries(numberMap)) {
      const value = params.get(param);
      if (value) {
        const parsed = parseFloat(value);
        if (!isNaN(parsed) && parsed > 0) {
          (prefill as Record<string, number>)[field] = parsed;
        }
      }
    }

    return prefill;
  }, []);
}
